"use client";

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Skeleton } from "@/app/components/ui/Skeleton";

export type TemplateLoadingApplication = "web" | "shop";

export type TemplateLoadingPhase =
  | "opening"
  | "applying"
  | "theming"
  | "creating";

export type TemplateLoadingScreenLabels = {
  brand: string;
  phases: Record<
    TemplateLoadingPhase,
    {
      description: string;
      title: string;
    }
  >;
  progress: string;
};

type TemplateLoadingTheme = {
  accent: string;
  secondary: string;
};

const templateLoadingThemes: Record<
  TemplateLoadingPhase,
  TemplateLoadingTheme
> = {
  applying: {
    accent: "rgb(var(--color-primary))",
    secondary: "rgb(var(--color-primary-hover))",
  },
  creating: {
    accent: "rgb(var(--color-success))",
    secondary: "rgb(var(--color-accent))",
  },
  opening: {
    accent: "rgb(var(--color-primary))",
    secondary: "rgb(var(--color-accent))",
  },
  theming: {
    accent: "rgb(var(--color-warning))",
    secondary: "rgb(var(--color-primary))",
  },
};

function TemplateLoadingFallbackMark({
  theme,
}: {
  theme: TemplateLoadingTheme;
}) {
  return (
    <span
      aria-hidden="true"
      className="grid h-12 w-12 shrink-0 place-items-center rounded-[17px] border border-border bg-surface shadow-panel"
      style={{
        background: `linear-gradient(145deg, color-mix(in srgb, ${theme.accent} 18%, transparent), color-mix(in srgb, ${theme.secondary} 10%, transparent)), rgb(var(--color-surface))`,
      }}
    >
      <span className="h-2.5 w-2.5 rounded-full bg-text-secondary" />
    </span>
  );
}

export function TemplateLoadingScreen({
  active = true,
  application = "web",
  applicationLogo,
  brandLogo,
  labels,
  onComplete,
  phase = "opening",
}: {
  active?: boolean;
  application?: TemplateLoadingApplication;
  applicationLogo?: ReactNode;
  brandLogo?: ReactNode;
  labels: TemplateLoadingScreenLabels;
  onComplete?: () => void;
  phase?: TemplateLoadingPhase;
}) {
  const [progress, setProgress] = useState(7);
  const [visible, setVisible] = useState(active);
  const startedAtRef = useRef(active ? Date.now() : 0);

  useEffect(() => {
    if (active) {
      if (!visible) {
        startedAtRef.current = Date.now();
        setProgress(7);
        setVisible(true);
      }

      const interval = window.setInterval(() => {
        setProgress((current) => {
          if (current < 90) {
            return Math.min(90, current + Math.max(0.8, (91 - current) * 0.13));
          }

          return Math.min(96, current + 0.16);
        });
      }, 110);

      return () => window.clearInterval(interval);
    }

    if (!visible) {
      return undefined;
    }

    let completionTimeout: number | null = null;
    const settleTimeout = window.setTimeout(() => {
      setProgress(100);
      const minimumVisibleTime = Math.max(
        0,
        520 - (Date.now() - startedAtRef.current),
      );

      completionTimeout = window.setTimeout(() => {
        setProgress(7);
        setVisible(false);
        startedAtRef.current = 0;
        onComplete?.();
      }, Math.max(320, minimumVisibleTime));
    }, 180);

    return () => {
      window.clearTimeout(settleTimeout);

      if (completionTimeout !== null) {
        window.clearTimeout(completionTimeout);
      }
    };
  }, [active, onComplete, visible]);

  if (!visible && !active) {
    return null;
  }

  const roundedProgress = Math.round(progress);
  const phaseLabels = labels?.phases?.[phase];
  const progressLabel = labels?.progress ?? "";
  const theme = templateLoadingThemes[phase];

  return (
    <div
      aria-busy="true"
      className="fixed inset-0 z-[1000] app-frame flex flex-col overflow-y-auto text-text"
      data-template-loading-phase={phase}
      data-template-loading-screen="true"
    >
      <div className="flex flex-1 flex-col rounded-[var(--app-frame-radius)] bg-[var(--app-canvas-color)]">
        <header className="flex h-24 shrink-0 items-center justify-center sm:h-28">
          {brandLogo ?? <span className="text-lg font-black tracking-tight">{labels?.brand ?? ""}</span>}
        </header>
        <div className="grid flex-1 place-items-center px-4 pb-12 sm:px-8">
          <section className="grid w-full max-w-4xl overflow-hidden rounded-[28px] bg-surface md:grid-cols-2">
            <div className="flex flex-col p-7 sm:p-10">
              <div aria-atomic="true" aria-live="polite" role="status">
                <span
                  aria-hidden="true"
                  className="grid h-20 w-20 place-items-center rounded-[28px] bg-primary/[0.06]"
                  data-template-loading-application={application}
                >
                  {applicationLogo ?? <TemplateLoadingFallbackMark theme={theme} />}
                </span>
                <h1 className="mt-7 text-balance text-2xl font-semibold tracking-tight text-text sm:text-3xl">
                  {phaseLabels?.title ?? ""}
                </h1>
                <p className="mt-3 max-w-sm text-pretty text-sm leading-6 text-text-secondary">
                  {phaseLabels?.description ?? ""}
                </p>
              </div>
              <div aria-live="off" className="mt-auto pt-10">
                <div className="mb-3 flex items-center justify-between gap-4 text-xs font-medium text-text-secondary">
                  <span>{progressLabel}</span>
                  <span className="tabular-nums text-primary">{roundedProgress}%</span>
                </div>
                <div
                  className="h-1.5 overflow-hidden rounded-full bg-primary/10"
                  role="progressbar"
                  aria-label={progressLabel || undefined}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={roundedProgress}
                >
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out motion-reduce:transition-none"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
            <div aria-hidden="true" className="m-4 hidden rounded-[24px] bg-bg-sunken p-5 md:block" data-template-loading-preview="true">
              <div className="mb-7 flex items-center justify-between">
                <Skeleton className="h-4 w-24 rounded-full" />
                <Skeleton className="h-8 w-16 rounded-full" />
              </div>
              <div className="rounded-[20px] bg-surface p-5">
                <Skeleton className="h-36 w-full rounded-2xl" />
                <Skeleton className="mt-6 h-4 w-2/3 rounded-full" />
                <Skeleton className="mt-3 h-3 w-full rounded-full" />
                <Skeleton className="mt-2 h-3 w-4/5 rounded-full" />
                <Skeleton className="mt-7 h-9 w-28 rounded-full" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
